import { GridColDef } from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';

import { ExtendedGridColDef } from '@Types/ExtendedGridColDef';
import CopyColumn from '@Components/table/CopyColumn';
import { ICustomerSelectionReport } from '@Types/customerType';

export const subareasByCustomerColumns: (GridColDef<ICustomerSelectionReport> &
  ExtendedGridColDef)[] = [
  {
    field: 'customerName',
    headerName: 'Customer',
    hideable: true,
    flex: 0.14,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value ?? ''}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.customerName,
  },
  {
    field: 'subareaName',
    headerName: 'Subarea Name',
    hideable: false,
    flex: 0.15,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.subareaName,
  },
  {
    field: 'subAreaId',
    headerName: 'Subarea ID',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      return <CopyColumn value={params.value} />;
    },
    valueGetter: (params) => params.row.subAreaId,
  },
  {
    field: 'publicId',
    headerName: 'Public Area Id',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      return <CopyColumn value={params.value} />;
    },
    valueGetter: (params) => params.row.publicId,
  },
  {
    field: 'availabilityStatus',
    headerName: 'Availability',
    hideable: true,
    flex: 0.1,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    // Only populated when the report is scoped to a single customer (live DMS lookup) - the
    // unfiltered "all customers" view intentionally shows a dash instead of triggering a live
    // occupancy call per subarea.
    valueGetter: (params) => params.row.availabilityStatus ?? '—',
  },
  {
    field: 'zoneInfo',
    headerName: 'Zone Info',
    hideable: true,
    flex: 0.16,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => {
      const { activeSessions, availableSpaces, percentFull } = params.row;

      if (activeSessions == null && availableSpaces == null && percentFull == null) {
        return '—';
      }

      const parts: string[] = [];

      if (activeSessions != null) parts.push(`${activeSessions} active`);
      if (availableSpaces != null) parts.push(`${availableSpaces} avail`);
      if (percentFull != null) parts.push(`${percentFull}% full`);

      return parts.join(' / ');
    },
  },
  {
    field: 'configId',
    headerName: 'Config ID',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      return <CopyColumn value={params.value} />;
    },
    valueGetter: (params) => params.row.configId,
  },
  {
    field: 'defaultTime',
    headerName: 'Default Time (minutes)',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.defaultTime,
  },
  {
    field: 'graceTime',
    headerName: 'Grace Time (minutes)',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value ?? ''}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.graceTime,
  },
  {
    field: 'convenienceFee',
    headerName: 'Convenience Fee',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.convenienceFee,
  },
  {
    field: 'reminderFee',
    headerName: 'Reminder Fee',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value ?? ''}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.reminderFee,
  },
  {
    field: 'hasVat',
    headerName: 'Has VAT',
    hideable: false,
    flex: 0.08,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => (params.row.hasVat ? 'Yes' : 'No'),
  },
  {
    field: 'isVisibleOnAppMap',
    headerName: 'Visible On App Map',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => (params.row.isVisibleOnAppMap ? 'Yes' : 'No'),
  },
  {
    field: 'hasMiniApp',
    headerName: 'Has Mini App',
    hideable: false,
    flex: 0.08,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => (params.row.hasMiniApp ? 'Yes' : 'No'),
  },
  {
    field: 'isEndSessionDisabled',
    headerName: 'End Session Disabled',
    hideable: false,
    flex: 0.1,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => (params.row.isEndSessionDisabled ? 'Yes' : 'No'),
  },
  {
    field: 'timeZoneId',
    headerName: 'Time Zone',
    hideable: false,
    flex: 0.12,
    renderCell: (params) => {
      const value = params.value;

      return (
        <Tooltip title={value ?? ''}>
          <span className='table-cell-truncate'>{value}</span>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.timeZoneId,
  },
];
